// src/components/admin/services/ServiceTable.jsx
import React from 'react';
import { FaEdit, FaTrash, FaStar, FaRegStar, FaImage, FaPhone, FaConciergeBell } from 'react-icons/fa';
import Pagination from '../../common/Pagination';
import Loading from '../../common/Loading';
import Swal from 'sweetalert2';

const ServiceTable = ({
  services,
  serviceTypes,
  onEdit,
  onDelete,
  onToggleFeatured,
  onStatusChange,
  loading,
  currentPage,
  totalPages,
  onPageChange
}) => {
  const getServiceTypeLabel = (type) => {
    const serviceType = serviceTypes.find(st => st.value === type);
    return serviceType ? serviceType.label : type;
  };

  const formatCurrency = (priceIdr, priceUsd) => {
    if (priceIdr && parseFloat(priceIdr) > 0) {
      return `Rp ${parseFloat(priceIdr).toLocaleString('id-ID')}`;
    }
    if (priceUsd && parseFloat(priceUsd) > 0) {
      return `$${parseFloat(priceUsd).toLocaleString('en-US')}`;
    }
    return 'Contact Us';
  };

  const getServiceImage = (service) => {
    if (service.image_path) {
      if (service.image_path.startsWith('http')) {
        return service.image_path;
      }
      return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/${service.image_path}`;
    }
    return null;
  };

  const isFeatured = (featured) => {
    return featured === true || featured === 1 || featured === '1';
  };

  // Handle delete with confirmation - WITH SUCCESS POPUP
  const handleDeleteClick = async (service) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Service?',
        html: `Are you sure you want to delete <strong>"${service.title}"</strong>?<br><br><small class="text-gray-600">This action cannot be undone.</small>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold',
          cancelButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });

      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Deleting...',
          html: 'Please wait a moment',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call parent delete
        await onDelete(service.id, service.title);
        
        // Show success popup
        await Swal.fire({
          title: 'Deleted!',
          html: `<strong>"${service.title}"</strong> has been successfully deleted.`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
      }
    } catch (error) {
      console.error('Error in delete process:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to delete service. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  // Handle featured toggle - WITH SUCCESS POPUP
  const handleFeatureToggle = async (service) => {
    try {
      const isCurrentlyFeatured = isFeatured(service.featured);
      const action = isCurrentlyFeatured ? 'remove from' : 'add to';
      
      const result = await Swal.fire({
        title: 'Featured Status',
        html: `Are you sure you want to ${action} featured for <strong>"${service.title}"</strong>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Continue',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold',
          cancelButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });

      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Updating...',
          html: 'Please wait a moment',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call parent function
        await onToggleFeatured(service.id, isCurrentlyFeatured, service.title);
        
        // Show success popup
        const successMessage = isCurrentlyFeatured 
          ? `<strong>"${service.title}"</strong> removed from featured list` 
          : `<strong>"${service.title}"</strong> added to featured list`;
        
        await Swal.fire({
          title: 'Success!',
          html: successMessage,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to change featured status. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  // Handle status change - WITH SUCCESS POPUP
  const handleStatusChange = async (service, newStatus) => {
    try {
      // Show loading
      Swal.fire({
        title: 'Updating Status...',
        html: 'Please wait a moment',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Call parent function
      await onStatusChange(service.id, newStatus, service.title);
      
      // Show success popup
      const statusText = newStatus === 'active' ? 'Active' : 'Inactive';
      await Swal.fire({
        title: 'Status Updated!',
        html: `<strong>"${service.title}"</strong> status changed to <strong>${statusText}</strong>`,
        icon: 'success',
        confirmButtonColor: '#10b981',
        confirmButtonText: 'OK',
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    } catch (error) {
      console.error('Error changing status:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to change service status. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  const getRowNumber = (index) => {
    const itemsPerPage = 10;
    return ((currentPage - 1) * itemsPerPage) + index + 1;
  };

  if (loading) return <Loading />;

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden">
      {/* Summary Info */}
      <div className="px-6 py-4 bg-gradient-to-r from-amber-50 via-amber-50/50 to-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaConciergeBell className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Total Services</h3>
              <p className="text-xs text-gray-600">Showing {services.length} service(s)</p>
            </div>
          </div>
          {totalPages > 0 && (
            <div className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-amber-50 via-amber-50/50 to-gray-50">
            <tr>
              <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                No
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Service
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {services.map((service, index) => {
              const imageUrl = getServiceImage(service);
              const rowNumber = getRowNumber(index);
              
              return (
                <tr key={service.id} className="hover:bg-amber-50/30 transition-colors duration-150">
                  <td className="px-4 py-4 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 text-amber-800 font-bold text-sm shadow-sm">
                      {rowNumber}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-16 w-16">
                        {imageUrl ? (
                          <img
                            className="h-16 w-16 rounded-xl object-cover shadow-sm border border-gray-200"
                            src={imageUrl}
                            alt={service.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`h-16 w-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm border border-gray-200 ${imageUrl ? 'hidden' : 'flex'}`}
                        >
                          <FaImage className="text-gray-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {service.title}
                        </div>
                        <div className="text-sm text-gray-600">
                          {service.description?.substring(0, 60)}
                          {service.description?.length > 60 ? '...' : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      {getServiceTypeLabel(service.service_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      {formatCurrency(service.price_idr, service.price_usd)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {service.phone ? (
                      <div className="flex items-center text-sm text-gray-700">
                        <FaPhone className="mr-2 text-gray-400" size={14} />
                        {service.phone}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">No phone</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-2">
                      <select
                        value={service.status}
                        onChange={(e) => handleStatusChange(service, e.target.value)}
                        className="text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all shadow-sm hover:border-amber-400"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      {isFeatured(service.featured) && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200 shadow-sm">
                          <FaStar className="mr-1.5" />
                          Featured
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={() => onEdit(service)}
                        className="inline-flex items-center justify-center p-2.5 border border-amber-300 shadow-sm text-sm font-semibold rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 transform hover:scale-110 active:scale-95"
                        title="Edit Service"
                      >
                        <FaEdit className="h-4 w-4" />
                      </button>

                      {/* Featured Toggle Button */}
                      <button
                        onClick={() => handleFeatureToggle(service)}
                        className={`inline-flex items-center justify-center p-2.5 border shadow-sm text-sm font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:scale-110 active:scale-95 ${
                          isFeatured(service.featured)
                            ? 'border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 hover:border-yellow-500 focus:ring-yellow-500'
                            : 'border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 focus:ring-gray-500'
                        }`}
                        title={isFeatured(service.featured) ? 'Remove from Featured' : 'Add to Featured'}
                      >
                        {isFeatured(service.featured) ? (
                          <FaStar className="h-4 w-4" />
                        ) : (
                          <FaRegStar className="h-4 w-4" />
                        )}
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteClick(service)}
                        className="inline-flex items-center justify-center p-2.5 border border-red-300 shadow-sm text-sm font-semibold rounded-lg text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 transform hover:scale-110 active:scale-95"
                        title="Delete Service"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {services.length === 0 && (
        <div className="text-center py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 mb-4">
            <FaConciergeBell className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No Services Found</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Start by adding your first service to manage your offerings.
          </p>
        </div>
      )}

      {services.length > 0 && (
        <div className="px-6 py-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 via-amber-50/20 to-gray-50">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages || 1}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ServiceTable;